import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Loader2, LogOut, Inbox, Star, MessageCircle, Check, X, Send, Image as ImageIcon, ChevronLeft, Trash2 } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/constants/testIds";

const TABS = [
  { id: "requests", label: "Quote Requests", Icon: Inbox, testid: TID.adminTabRequests },
  { id: "reviews", label: "Reviews", Icon: Star, testid: TID.adminTabReviews },
  { id: "chats", label: "Chats", Icon: MessageCircle, testid: TID.adminTabChats },
];

const StarRow = ({ value }) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map((i) => (
      <Star key={i} size={14} strokeWidth={0} fill={i <= value ? "#D4AF37" : "#3a3a3a"} />
    ))}
  </div>
);

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("requests");

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;
  }
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 lg:min-h-screen bg-[#080808] border-b lg:border-r lg:border-b-0 border-white/5 p-5 flex lg:flex-col gap-2 lg:gap-1">
        <div className="hidden lg:block mb-8">
          <div className="font-serif-display text-xl gold-grad-text">Premier Admin</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mt-1">Control Room</div>
        </div>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={t.testid}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors flex-1 lg:flex-none ${
              tab === t.id ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30" : "text-white/70 hover:bg-white/5"
            }`}
          >
            <t.Icon size={16} strokeWidth={1.4} />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
        <button
          onClick={logout}
          data-testid={TID.adminLogout}
          className="hidden lg:flex mt-auto items-center gap-3 px-4 py-3 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5"
        >
          <LogOut size={16} strokeWidth={1.4} /> Sign Out
        </button>
      </aside>

      {/* Main */}
      <main className="flex-grow p-6 lg:p-10 max-w-full overflow-x-hidden">
        {tab === "requests" && <QuoteRequestsTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "chats" && <ChatsTab />}
        <button
          onClick={logout}
          className="lg:hidden mt-8 btn-ghost w-full justify-center"
        >
          <LogOut size={16} /> Sign Out
        </button>
      </main>
    </div>
  );
}

/* ---------------- Quote Requests ---------------- */
function QuoteRequestsTab() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quote-requests");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (active) return <RequestDetail item={active} onBack={() => { setActive(null); load(); }} />;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif-display text-3xl lg:text-4xl">Quote Requests</h1>
          <p className="text-white/50 text-sm mt-1">{items.length} total · {items.filter(i => i.status === "pending").length} pending</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#D4AF37]" /></div>
      ) : items.length === 0 ? (
        <div className="card-surface rounded-2xl p-12 text-center text-white/50">No requests yet.</div>
      ) : (
        <div className="space-y-3" data-testid={TID.adminRequestsList}>
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => setActive(it)}
              data-testid={`${TID.adminViewRequest}-${it.id}`}
              className="w-full card-surface rounded-xl p-5 text-left flex flex-col sm:flex-row sm:items-center gap-4 hover:border-[#D4AF37]/40"
            >
              <div className="flex-grow">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-medium">{it.name}</div>
                  <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full ${
                    it.status === "quoted"
                      ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30"
                      : "bg-white/5 text-white/60 border border-white/10"
                  }`}>{it.status}</span>
                </div>
                <div className="text-sm text-white/55 mt-1">
                  {labelService(it.service_type)} · {it.email} · {it.phone}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {it.image_paths?.length > 0 && (
                  <span className="text-xs text-white/40 flex items-center gap-1"><ImageIcon size={12} /> {it.image_paths.length}</span>
                )}
                <div className="text-xs text-white/40">{new Date(it.created_at).toLocaleDateString()}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function labelService(s) {
  return ({
    commercial: "Commercial",
    move_in_out: "Move In/Out",
    post_construction: "Post-Construction",
  }[s] || s);
}

function RequestDetail({ item, onBack }) {
  const [quote, setQuote] = useState({
    amount: item.quote?.amount || "",
    scope: item.quote?.scope || "",
    notes: item.quote?.notes || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!quote.amount || !quote.scope) {
      toast.error("Amount and scope are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/quote-requests/${item.id}/quote`, {
        amount: parseFloat(quote.amount),
        scope: quote.scope,
        notes: quote.notes,
      });
      toast.success(data.email_sent ? "Quote sent — email delivered." : "Quote saved. (Email not sent — set RESEND_API_KEY)");
      onBack();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteRequest = async () => {
    if (!window.confirm(`Permanently delete this request from "${item.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/quote-requests/${item.id}`);
      toast.success("Request deleted.");
      onBack();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-white/60 hover:text-white">
          <ChevronLeft size={16}/> Back to requests
        </button>
        <button
          onClick={deleteRequest}
          data-testid={`admin-delete-request-${item.id}`}
          className="px-3 py-2 rounded-lg bg-red-600/20 border border-red-600/40 text-red-300 hover:bg-red-600/30 text-xs flex items-center gap-2"
        >
          <Trash2 size={14} /> Delete Request
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Details */}
        <div className="card-surface rounded-2xl p-7">
          <div className="overline text-[#D4AF37] mb-3">Client</div>
          <h2 className="font-serif-display text-2xl">{item.name}</h2>
          <div className="text-sm text-white/65 mt-2">{item.email} · {item.phone}</div>

          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <Field label="Service">{labelService(item.service_type)}</Field>
            <Field label="Status">{item.status}</Field>
            <Field label="Property Type">{item.property_type || "—"}</Field>
            <Field label="Square Footage">{item.square_footage || "—"}</Field>
            <Field label="Preferred Date">{item.preferred_date || "—"}</Field>
            <Field label="Submitted">{new Date(item.created_at).toLocaleString()}</Field>
            <div className="col-span-2">
              <Field label="Address">{item.address || "—"}</Field>
            </div>
            <div className="col-span-2">
              <Field label="Notes">{item.message || "—"}</Field>
            </div>
          </div>

          {item.image_paths?.length > 0 && (
            <div className="mt-6">
              <div className="overline text-white/50 mb-3">Photos ({item.image_paths.length})</div>
              <div className="grid grid-cols-3 gap-2">
                {item.image_paths.map((p) => (
                  <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden border border-white/10 bg-black/50">
                    <img src={`${API}/files/${p}`} alt="upload" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
              <p className="text-[11px] text-white/40 mt-2">Images are auto-deleted after quote is sent or 30 days, whichever comes first.</p>
            </div>
          )}
        </div>

        {/* Quote form */}
        <div className="card-surface rounded-2xl p-7">
          <div className="overline text-[#D4AF37] mb-3">{item.status === "quoted" ? "Re-Send Quote" : "Submit Quote"}</div>
          <h2 className="font-serif-display text-2xl">Custom Estimate</h2>

          {item.status === "quoted" && item.quote && (
            <div className="mt-4 p-4 bg-black/40 rounded-lg border border-white/5 text-sm">
              <div className="text-white/50">Previously sent {new Date(item.quoted_at || item.quote.submitted_at).toLocaleString()}</div>
              <div className="text-[#D4AF37] font-serif-display text-2xl mt-1">${Number(item.quote.amount).toFixed(2)}</div>
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-6">
            <div>
              <label className="overline text-white/50 block mb-2">Total Amount (USD) *</label>
              <input
                type="number"
                step="0.01"
                className="input-line"
                value={quote.amount}
                onChange={(e) => setQuote({...quote, amount: e.target.value})}
                data-testid={TID.adminQuoteAmount}
                required
              />
            </div>
            <div>
              <label className="overline text-white/50 block mb-2">Scope of Work *</label>
              <textarea
                className="input-line"
                rows={5}
                value={quote.scope}
                onChange={(e) => setQuote({...quote, scope: e.target.value})}
                placeholder="Itemize what's included..."
                data-testid={TID.adminQuoteScope}
                required
              />
            </div>
            <div>
              <label className="overline text-white/50 block mb-2">Internal Notes</label>
              <textarea
                className="input-line"
                rows={3}
                value={quote.notes}
                onChange={(e) => setQuote({...quote, notes: e.target.value})}
                data-testid={TID.adminQuoteNotes}
              />
            </div>
            <button type="submit" disabled={submitting} className="btn-gold w-full justify-center" data-testid={TID.adminQuoteSubmit}>
              {submitting ? <Loader2 className="animate-spin" size={16}/> : <>Send Quote <Send size={14}/></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="overline text-white/40 mb-1">{label}</div>
    <div className="text-white/85">{children}</div>
  </div>
);

/* ---------------- Reviews ---------------- */
function ReviewsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reviews");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { status });
      toast.success(`Review ${status}.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteReview = async (id, name) => {
    if (!window.confirm(`Permanently delete review from "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <h1 className="font-serif-display text-3xl lg:text-4xl mb-2">Reviews</h1>
      <p className="text-white/50 text-sm mb-8">{items.filter(r => r.status === "pending").length} pending · {items.filter(r => r.status === "approved").length} approved</p>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#D4AF37]" /></div>
      ) : items.length === 0 ? (
        <div className="card-surface rounded-2xl p-12 text-center text-white/50">No reviews yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="card-surface rounded-xl p-5 flex flex-col lg:flex-row gap-5">
              <div className="flex-grow">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-medium">{r.name}</div>
                  <StarRow value={r.rating} />
                  <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full ${
                    r.status === "approved" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" :
                    r.status === "rejected" ? "bg-red-500/10 text-red-300 border border-red-500/30" :
                    "bg-white/5 text-white/60 border border-white/10"
                  }`}>{r.status}</span>
                </div>
                {r.title && <h3 className="font-serif-display text-lg mt-2">{r.title}</h3>}
                <p className="text-white/65 text-sm mt-2 leading-relaxed">"{r.body}"</p>
                <div className="text-xs text-white/40 mt-2">{new Date(r.created_at).toLocaleString()}</div>
              </div>
              <div className="flex gap-2 lg:flex-col">
                {r.status !== "approved" && (
                  <button onClick={() => updateStatus(r.id, "approved")} data-testid={`${TID.adminApproveReview}-${r.id}`} className="px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-1">
                    <Check size={14} /> Approve
                  </button>
                )}
                {r.status !== "rejected" && (
                  <button onClick={() => updateStatus(r.id, "rejected")} data-testid={`${TID.adminRejectReview}-${r.id}`} className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-center gap-1">
                    <X size={14} /> Reject
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Chats ---------------- */
function ChatsTab() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const messagesEndRef = useRef(null);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/admin/chat/sessions");
      setSessions(data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    loadSessions();
    const interval = setInterval(loadSessions, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!active) return;
    let alive = true;
    const fetchMsgs = async () => {
      try {
        const { data } = await api.get(`/chat/messages/${active.id}`);
        if (alive) setMessages(data);
      } catch (_) {}
    };
    fetchMsgs();
    const interval = setInterval(fetchMsgs, 3000);
    api.post(`/admin/chat/sessions/${active.id}/read`).catch(() => {});
    return () => { alive = false; clearInterval(interval); };
  }, [active]);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      const { data } = await api.post("/chat/messages", {
        session_id: active.id,
        sender: "admin",
        sender_name: "Premier Team",
        text: text.trim(),
      });
      setMessages((prev) => [...prev, data]);
      setText("");
      loadSessions();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <h1 className="font-serif-display text-3xl lg:text-4xl mb-2">Live Chats</h1>
      <p className="text-white/50 text-sm mb-8">{sessions.length} conversation{sessions.length === 1 ? "" : "s"}</p>

      <div className="grid lg:grid-cols-[320px_1fr] gap-4 lg:h-[70vh]">
        <div className="card-surface rounded-xl overflow-y-auto max-h-[40vh] lg:max-h-full">
          {sessions.length === 0 ? (
            <div className="p-6 text-white/40 text-sm">No conversations yet.</div>
          ) : sessions.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s)}
              className={`w-full text-left p-4 border-b border-white/5 transition-colors ${active?.id === s.id ? "bg-[#D4AF37]/10" : "hover:bg-white/5"}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm truncate">{s.name || "Guest"}</div>
                {s.unread_count > 0 && (
                  <span className="bg-[#D4AF37] text-black text-[10px] font-bold rounded-full px-2 py-0.5">{s.unread_count}</span>
                )}
              </div>
              <div className="text-xs text-white/50 truncate mt-1">
                {s.last_sender === "admin" ? "You: " : ""}{s.last_message || "No messages yet"}
              </div>
              <div className="text-[10px] text-white/30 mt-1">{new Date(s.updated_at).toLocaleString()}</div>
            </button>
          ))}
        </div>

        <div className="card-surface rounded-xl flex flex-col min-h-[400px]">
          {!active ? (
            <div className="flex-grow flex items-center justify-center text-white/40 text-sm">Select a conversation</div>
          ) : (
            <>
              <div className="p-4 border-b border-white/5">
                <div className="font-medium">{active.name}</div>
                <div className="text-xs text-white/40">{active.email || "—"}</div>
              </div>
              <div className="flex-grow overflow-y-auto p-5 space-y-3 bg-[#080808]">
                {messages.map((m) => (
                  <div key={m.id || m.created_at} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${
                      m.sender === "admin" ? "bg-[#D4AF37] text-black rounded-br-sm" : "bg-[#1a1a1a] text-white border border-white/5 rounded-bl-sm"
                    }`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-white/5 flex gap-2">
                <input
                  className="flex-grow bg-transparent text-white text-sm outline-none px-3 py-2"
                  placeholder="Type a reply..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
                <button type="submit" disabled={!text.trim()} className="h-10 w-10 rounded-full bg-[#D4AF37] text-black flex items-center justify-center disabled:opacity-40">
                  <Send size={16}/>
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
